import * as Sentry from "@sentry/react";
import { isBrowserExtensionEvent, isForeignScriptEvent, scrubVehicleIdentifiers } from './utils/sentryFilters';
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import './i18n';
import reportWebVitals from './reportWebVitals';
import axios from 'axios';
import { getOrganizationId, useUserStore } from './store';
import i18n from 'i18next';
import ErrorBoundary from './components/shared/ErrorBoundary';

// Fix for DOM reconciliation errors (e.g., from browser extensions modifying DOM)
// See: https://github.com/facebook/react/issues/11538#issuecomment-417504600
if (typeof Node === 'function' && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function(child) {
    if (child.parentNode !== this) {
      if (console) {
        console.warn('DOM reconciliation issue: Cannot remove a child from a different parent');
      }
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (console) {
        console.warn('DOM reconciliation issue: Cannot insert before a reference node from a different parent');
      }
      return newNode;
    }
    return originalInsertBefore.apply(this, arguments);
  };
}

// Initialize Sentry for error tracking
Sentry.init({
  dsn: "https://dd42dd79baac3368d2153e6aef34b328@o4510661916491776.ingest.de.sentry.io/4510662150258768",
  sendDefaultPii: false,
  enabled: process.env.NODE_ENV === 'production',
  // Drop non-actionable noise: offline/cancelled requests and third-party
  // browser-extension errors are not our bugs (VERSTAANI-FRONTEND-K / -M).
  ignoreErrors: [
    'Network Error',
    'Request aborted',
    /timeout of \d+ms exceeded/,
    'AbortError',
    'CanceledError',
    'runtime.sendMessage',
    'Extension context invalidated',
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
  ],
  beforeSend(event, hint) {
    const err = hint && hint.originalException;
    // Axios network/cancel/timeout errors: offline users, aborted navigations.
    const code = err && err.code;
    if (code === 'ERR_NETWORK' || code === 'ERR_CANCELED' || code === 'ECONNABORTED') {
      return null;
    }
    const name = err && err.name;
    if (name === 'CanceledError' || name === 'AbortError') {
      return null;
    }
    // Errors thrown from injected browser extensions (frames in extension:// URLs).
    if (isBrowserExtensionEvent(event)) {
      return null;
    }
    // Errors thrown by scripts the browser injects into the page (Chrome for
    // iOS __gCrWeb autofill etc.): the stack never touches /static/js/, so
    // they are not ours and cannot be source-mapped. See utils/sentryFilters.
    if (isForeignScriptEvent(event)) {
      return null;
    }
    // Registration numbers and VINs never leave the browser. Fail closed.
    try {
      return scrubVehicleIdentifiers(event);
    } catch (e) {
      return null;
    }
  },
  // XHR/fetch/navigation breadcrumbs carry URLs like ?registration_number=ABC-123.
  beforeBreadcrumb(breadcrumb) {
    try {
      return scrubVehicleIdentifiers(breadcrumb);
    } catch (e) {
      return null;
    }
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));

// Set Axios defaults - built-in CSRF handling
axios.defaults.withCredentials = true;
axios.defaults.xsrfCookieName = 'csrftoken';
axios.defaults.xsrfHeaderName = 'X-CSRFToken';

// A refused write (403 with the backend's translated reason) is shown by App's PermissionDeniedSnackbar,
// so a button that a missing permission did not hide never fails silently.
axios.interceptors.response.use(undefined, (error) => {
  const method = (error.config?.method || 'get').toLowerCase();
  const detail = error.response?.data?.detail;
  // An admin turned on "require two-factor authentication" while this user was logged in without
  // it: refetch the user so App swaps to the setup screen (MfaSetupGate).
  if (error.response?.status === 403 && error.response?.data?.code === 'mfa_setup_required') {
    const { user, fetchUser } = useUserStore.getState();
    if (!user?.mfa_setup_required) {
      fetchUser().catch(() => {});
    }
    return Promise.reject(error);
  }
  if (error.response?.status === 403 && method !== 'get' && typeof detail === 'string') {
    window.dispatchEvent(new CustomEvent('verstaani:permission-denied', { detail }));
  }
  return Promise.reject(error);
});

// Add interceptor for organization
axios.interceptors.request.use((config) => {
  const orgId = getOrganizationId();

  // Backend messages (permission refusals, validation errors) are translated by Django's
  // LocaleMiddleware from this header, so they follow the UI language, not the browser's.
  if (i18n.language) {
    config.headers = config.headers || {};
    config.headers['Accept-Language'] = i18n.language;
  }

  // Skip adding organization for user endpoint (needed to fetch orgs first)
  if (orgId && !config.url.includes('/api/users/me/')) {
    // Add to query params for GET and DELETE requests
    if (config.method === 'get' || config.method === 'delete' || !config.method) {
      config.params = config.params || {};
      if (!config.params.organization) {
        config.params.organization = orgId;
      }
    }
    // Add to body for POST/PUT/PATCH requests
    else if (['post', 'put', 'patch'].includes(config.method)) {
      if (config.data instanceof FormData) {
        if (!config.data.has('organization')) {
          config.data.append('organization', orgId);
        }
      } else if (config.data && typeof config.data === 'object' && !Array.isArray(config.data)) {
        if (!config.data.organization) {
          config.data.organization = orgId;
        }
      } else if (!config.data) {
        config.data = { organization: orgId };
      }
    }
  }

  return config;
});

root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please refresh the page.</p>}>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
